import { Center, Loader } from "@mantine/core";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";

import { ClientOnly, Hero } from "@mountayaapp/markdoc/renderers";

import { RedocNav } from "./RedocNav";
import { readSpecSectionsWhenReady, type SpecSection } from "./sections";
import "./style.css";

// Redoc bundles a large OpenAPI renderer and touches the DOM directly, so it is
// code-split and loaded only in the browser when a reference is on screen.
const Client = lazy(() => import("./Client").then((m) => ({ default: m.RedocClient })));

/**
 * RedocSpec names the bundled OpenAPI document to render: the Map Studio API or
 * the public Tile API.
 */
type RedocSpec = "api" | "tiles";

/**
 * RedocProps configure the {% redoc %} tag. `spec` selects which bundled OpenAPI
 * document under the static descriptions tree to render. `title` and `lead` feed
 * the shared Hero that opens the reference, giving the page the same
 * title-and-overview opening as the rest of the documentation.
 */
interface RedocProps {
  lead?: string;
  spec?: RedocSpec;
  title?: string;
}

/**
 * fallback is the placeholder shown while the reference is server-rendered and
 * during the client load — a centered spinner in a fixed-height box so the column
 * holds a stable footprint while the reference swaps in.
 */
const fallback = (
  <Center h={320}>
    <Loader />
  </Center>
);

/**
 * Redoc is the {% redoc %} renderer the documentation app injects. It frames the
 * reference the way every other page is framed: a navigation rail on the left and
 * the reading column beside it. The rail lists the specification's tags and
 * operations, read back off the document once Redoc has rendered it, which is
 * what these pages had no navigation of at all before — Redoc's own menu is
 * collapsed in the theme options in favour of it.
 *
 * The reading column's first child is the shared Hero (title and one lead line)
 * and its body is the reference itself, so the opening and the spec scroll
 * together as one page. The Hero renders in the static prerender and during the
 * lazy load — it sits above the Suspense boundary but inside the scroll — so the
 * title and overview are present immediately while the heavy client loads only in
 * the browser, behind the client-only gate and a Suspense boundary. That column
 * owns the vertical scroll; Redoc keeps its own sticky right panel inside it, and
 * the rail scrolls independently beside it.
 */
export const Redoc = ({ lead, spec = "tiles", title }: RedocProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [sections, setSections] = useState<SpecSection[]>([]);
  const cancelRead = useRef<() => void>(undefined);

  const handleLoaded = useCallback(() => {
    cancelRead.current?.();
    cancelRead.current = readSpecSectionsWhenReady(scrollRef.current, setSections);
  }, []);

  useEffect(() => () => cancelRead.current?.(), []);

  return (
    <div className="redoc-reference">
      <RedocNav scrollRef={scrollRef} sections={sections} />
      <div className="redoc-scroll" ref={scrollRef}>
        {title || lead ? (
          <header className="redoc-header">
            <Hero subtitle={lead} title={title} />
          </header>
        ) : null}
        <ClientOnly fallback={fallback}>
          <Suspense fallback={fallback}>
            <Client onLoaded={handleLoaded} specUrl={`/descriptions/openapi/${spec}/index.yaml`} />
          </Suspense>
        </ClientOnly>
      </div>
    </div>
  );
};
